import {useCallback, useEffect, useRef, useState} from 'react'
import type {UploadCallbacks, UploadConfig} from '@github-ui/batch-upload'
import {upload} from '@github-ui/batch-upload'
import Batch from '@github-ui/batch-upload/batch'
import {Attachment} from './attachment'

function hasFile(transfer: DataTransfer): boolean {
  return Array.from(transfer.types).includes('Files')
}

const imageTypes = /^image\/(gif|png|jpeg)$/

function pastedFile(items: DataTransferItemList): File | null {
  for (const item of items) {
    if (item.kind === 'file' && imageTypes.test(item.type)) {
      return item.getAsFile()
    }
  }
  return null
}

export interface FileAttachmentProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** Content rendered inside the drop zone. */
  children: React.ReactNode
  /** Enable directory/folder traversal on drop. */
  directory?: boolean
  /** Scope file input handling to a specific input by ID. */
  inputId?: string
  /** Called when files are accepted. */
  onAccept?: (attachments: Attachment[]) => void
  /**
   * Cancelable callback before accepting files.
   * Return `false` to prevent accepting.
   */
  onBeforeAccept?: (attachments: Attachment[]) => boolean
  /** Upload config for batch uploading. When provided, accepted files are uploaded automatically. */
  uploadConfig?: UploadConfig
  /** Callbacks for upload lifecycle events (state changes, progress, completion, errors). */
  uploadCallbacks?: UploadCallbacks
  /** Whether to prevent accidental file drops on the document outside this drop zone. Defaults to `true` when `uploadConfig` is provided. */
  preventAccidentalDrops?: boolean
}

export function FileAttachment({
  children,
  directory = false,
  inputId,
  onAccept,
  onBeforeAccept,
  uploadConfig,
  uploadCallbacks,
  preventAccidentalDrops,
  ...rest
}: FileAttachmentProps) {
  const [hover, setHover] = useState(false)
  const draggingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const shouldPreventDrops = preventAccidentalDrops ?? !!uploadConfig

  // Prevent accidental drops outside the drop zone
  useEffect(() => {
    if (!shouldPreventDrops) return

    function onDocumentDrop(event: DragEvent) {
      const transfer = event.dataTransfer
      if (transfer && hasFile(transfer)) {
        event.preventDefault()
      }
    }

    function onDocumentDragover(event: DragEvent) {
      const transfer = event.dataTransfer
      if (transfer && hasFile(transfer)) {
        event.preventDefault()
      }
    }

    document.addEventListener('drop', onDocumentDrop)
    document.addEventListener('dragover', onDocumentDragover)
    return () => {
      document.removeEventListener('drop', onDocumentDrop)
      document.removeEventListener('dragover', onDocumentDragover)
    }
  }, [shouldPreventDrops])

  const attach = useCallback(
    async (transferred: File[] | Attachment[] | FileList | DataTransfer) => {
      const attachments =
        transferred instanceof DataTransfer
          ? await Attachment.traverse(transferred, directory)
          : Attachment.from(transferred)

      // Filter resulted in no files (e.g. hidden dot files)
      if (attachments.length === 0) {
        uploadCallbacks?.onStateChange('is-hidden-file')
        return
      }

      const accepted = onBeforeAccept ? onBeforeAccept(attachments) : true
      if (!accepted) return

      onAccept?.(attachments)

      if (uploadConfig && uploadCallbacks) {
        upload(new Batch(attachments), uploadConfig, uploadCallbacks)
      }
    },
    [directory, onAccept, onBeforeAccept, uploadConfig, uploadCallbacks],
  )

  const handleDragEnter = useCallback(
    (event: React.DragEvent) => {
      if (draggingTimerRef.current) {
        clearTimeout(draggingTimerRef.current)
      }
      draggingTimerRef.current = setTimeout(() => setHover(false), 200)

      const transfer = event.dataTransfer
      if (!transfer || !hasFile(transfer)) return

      transfer.dropEffect = 'copy'
      setHover(true)
      event.preventDefault()
    },
    [setHover],
  )

  const handleDragLeave = useCallback(
    (event: React.DragEvent) => {
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'none'
      }
      setHover(false)
      event.stopPropagation()
      event.preventDefault()
    },
    [setHover],
  )

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      setHover(false)
      const transfer = event.dataTransfer
      if (!transfer || !hasFile(transfer)) return

      attach(transfer)
      event.stopPropagation()
      event.preventDefault()
    },
    [attach, setHover],
  )

  const handlePaste = useCallback(
    (event: React.ClipboardEvent) => {
      const clipboardData = event.clipboardData
      if (!clipboardData?.items) return

      const file = pastedFile(clipboardData.items)
      if (!file) return

      attach([file])
      event.preventDefault()
    },
    [attach],
  )

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const input = event.target
      if (inputId && input.id !== inputId) return

      const files = input.files
      if (!files || files.length === 0) return

      attach(files)
      input.value = ''
    },
    [attach, inputId],
  )

  return (
    <div
      {...rest}
      data-hover={hover || undefined}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onPaste={handlePaste}
      onChange={handleChange}
    >
      {children}
    </div>
  )
}
